import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import { LocationOn, Email, Phone } from "@mui/icons-material";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import PageTitle from "../components/PageTitle";
import privacyBanner from "../assets/images/privacy.avif";

const PrivacyPolicy = () => {
  const sections = [
    {
      title: "1. About RestaurantWala",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala is a QR-based restaurant ordering and restaurant
            management platform that enables restaurants to digitize their
            operations and manage services including digital menus, QR
            ordering, orders, billing, staff management, offers, inventory,
            customer ratings, and related restaurant operations.
          </Typography>
          <Typography paragraph>
            RestaurantWala may serve different categories of users, including
            restaurant owners, restaurant staff, and restaurant customers.
          </Typography>
        </>
      ),
    },
    {
      title: "2. Information We Collect",
      content: (
        <>
          <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
            2.1 Restaurant and Business Information
          </Typography>
          <Typography paragraph>
            When a restaurant registers with RestaurantWala, we may collect:
          </Typography>
          <BulletList
            items={[
              "Restaurant name",
              "Restaurant address",
              "Restaurant contact number",
              "Restaurant email address",
              "Owner or authorized representative information",
              "Restaurant logo and images",
              "Business-related information",
              "Menu and category information",
              "Food item names, descriptions, prices and images",
              "Table information",
              "Restaurant operating information",
              "Payment-related configuration information",
              "Other information provided by the restaurant during registration or use of the platform",
            ]}
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            2.2 Account Information
          </Typography>
          <Typography paragraph>
            For restaurant accounts and authorized users, we may collect:
          </Typography>
          <BulletList
            items={[
              "Name",
              "Email address",
              "Mobile number",
              "Login credentials",
              "Role or staff designation",
              "Account and profile information",
            ]}
          />
          <Typography paragraph>
            Passwords are intended to be stored and handled using appropriate
            security practices and should not be stored in plain text.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            2.3 Customer and Order Information
          </Typography>
          <Typography paragraph>
            When a customer places an order through a RestaurantWala-powered
            digital menu or QR ordering system, information associated with the
            order may include:
          </Typography>
          <BulletList
            items={[
              "Customer name, where provided",
              "Mobile number, where provided",
              "Delivery address, where applicable",
              "Order details",
              "Selected food items",
              "Quantity and price",
              "Table number or QR/table identification",
              "Order type such as DINE_IN, TAKEAWAY, or DELIVERY",
              "Order status",
              "Payment method or payment-related information provided to the restaurant",
              "Customer feedback or rating",
            ]}
          />
          <Typography paragraph>
            Customers may be able to use QR-based digital menus without
            creating a RestaurantWala account, depending on the restaurant's
            configuration.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            2.4 Contact Information
          </Typography>
          <Typography paragraph>
            If you contact us through our contact form, email, telephone,
            WhatsApp, or other communication channels, we may collect:
          </Typography>
          <BulletList
            items={[
              "Name",
              "Email address",
              "Phone number",
              "Subject",
              "Message or enquiry details",
              "Information voluntarily provided during the communication",
            ]}
          />

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            2.5 Technical Information
          </Typography>
          <Typography paragraph>
            When you use our website or Services, certain technical information
            may be automatically collected, such as:
          </Typography>
          <BulletList
            items={[
              "IP address",
              "Browser type",
              "Device type",
              "Operating system",
              "General usage information",
              "Pages or features accessed",
              "Date and time of access",
              "Error and diagnostic information",
            ]}
          />
          <Typography paragraph>
            We may use cookies or similar technologies where applicable.
          </Typography>
        </>
      ),
    },
    {
      title: "3. How We Use Your Information",
      content: (
        <>
          <Typography paragraph>
            We may use collected information for purposes including:
          </Typography>
          <BulletList
            items={[
              "Creating and managing restaurant accounts",
              "Providing RestaurantWala's restaurant management services",
              "Providing digital menu and QR ordering functionality",
              "Processing and managing restaurant orders",
              "Managing tables and order types",
              "Providing billing and invoice functionality",
              "Managing restaurant subscriptions",
              "Providing customer support",
              "Responding to enquiries and communications",
              "Sending service-related notifications",
              "Improving our Services and user experience",
              "Maintaining platform security",
              "Detecting, preventing, and addressing fraud, misuse, or unauthorized activity",
              "Maintaining technical records and system logs",
              "Managing staff roles and permissions",
              "Supporting restaurant operations",
              "Providing offers, deals, ratings, inventory, and other enabled platform features",
              "Complying with applicable legal and regulatory obligations",
            ]}
          />
          <Typography paragraph>
            We aim to process personal data only for specified and legitimate
            purposes and to the extent reasonably necessary for providing or
            improving the Services.
          </Typography>
        </>
      ),
    },
    {
      title: "4. Restaurant Data and Customer Data",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala acts as a technology platform that enables
            restaurants to manage their operations.
          </Typography>
          <Typography paragraph>
            Restaurants may independently collect and process customer
            information when customers interact with a particular restaurant
            through RestaurantWala.
          </Typography>
          <Typography paragraph>
            For example, a restaurant may receive customer order information
            necessary to prepare and fulfil an order.
          </Typography>
          <Typography paragraph>
            Where a restaurant determines the purpose and manner in which
            customer information is used for its own business operations, the
            restaurant may have separate privacy responsibilities.
          </Typography>
          <Typography paragraph>
            Restaurant users are responsible for ensuring that the information
            they collect and use through RestaurantWala is handled in accordance
            with applicable laws and their own privacy practices.
          </Typography>
        </>
      ),
    },
    {
      title: "5. QR Ordering",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala enables restaurants to provide QR-based digital menus
            and ordering.
          </Typography>
          <Typography paragraph>
            Customers may scan a restaurant's QR code to access the digital menu
            and, depending on the restaurant's configuration, place an order.
          </Typography>
          <Typography paragraph>The QR code may identify information such as:</Typography>
          <BulletList
            items={[
              "Restaurant",
              "Table",
              "Ordering location",
              "Ordering session",
            ]}
          />
          <Typography paragraph>
            QR codes themselves are not intended to provide customers with
            access to another customer's private information.
          </Typography>
        </>
      ),
    },
    {
      title: "6. Payments",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala may allow restaurants to configure and record
            different payment methods, including:
          </Typography>
          <BulletList items={["Cash", "UPI", "Card", "Other payment methods supported by the restaurant"]} />
          <Typography paragraph>
            Where RestaurantWala does not directly process a payment, the actual
            payment transaction may occur through the restaurant, bank, UPI
            provider, card provider, or another payment service.
          </Typography>
          <Typography paragraph>
            We do not intend to collect or store complete card numbers, CVV
            numbers, UPI PINs, passwords, or other sensitive payment credentials
            unless a specific payment integration expressly requires such
            processing.
          </Typography>
          <Typography paragraph>
            Users should never provide passwords, UPI PINs, OTPs, CVV numbers,
            or other confidential authentication information to RestaurantWala
            through normal communication channels.
          </Typography>
        </>
      ),
    },
    {
      title: "7. WhatsApp and Other Communication Services",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala may provide integrations or links that allow
            restaurants to communicate about orders or support through services
            such as WhatsApp.
          </Typography>
          <Typography paragraph>
            When you use a third-party communication service, your interaction
            may also be governed by that provider's privacy policy and terms.
          </Typography>
          <Typography paragraph>
            RestaurantWala does not control the privacy practices of third-party
            communication platforms.
          </Typography>
        </>
      ),
    },
    {
      title: "8. Cookies and Similar Technologies",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala may use cookies, local storage, session technologies,
            and similar technologies to:
          </Typography>
          <BulletList
            items={[
              "Keep users signed in",
              "Maintain sessions",
              "Remember preferences",
              "Improve website performance",
              "Understand how users interact with the Services",
              "Improve security",
              "Analyze platform usage where applicable",
            ]}
          />
          <Typography paragraph>
            You may be able to control cookies through your browser settings.
            Disabling certain cookies may affect some functionality of the
            Services.
          </Typography>
        </>
      ),
    },
    {
      title: "9. Third-Party Services",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala may use third-party service providers to operate and
            improve its Services.
          </Typography>
          <Typography paragraph>
            Depending on the features being used, these may include providers
            for:
          </Typography>
          <BulletList
            items={[
              "Cloud hosting",
              "Database infrastructure",
              "Image and file storage",
              "Email communication",
              "Analytics",
              "Payment processing",
              "Communication services",
              "Authentication",
              "Security and monitoring",
            ]}
          />
          <Typography paragraph>
            Third-party providers may process information on our behalf where
            necessary to provide their services.
          </Typography>
          <Typography paragraph>
            We aim to work with service providers that maintain appropriate
            security and privacy practices.
          </Typography>
        </>
      ),
    },
    {
      title: "10. Data Sharing",
      content: (
        <>
          <Typography paragraph>
            We do not sell personal information as a standalone commercial
            product.
          </Typography>
          <Typography paragraph>
            We may share information when reasonably necessary with:
          </Typography>
          <BulletList
            items={[
              "The restaurant associated with an order or account",
              "Authorized restaurant staff",
              "Service providers supporting our platform",
              "Payment or financial service providers where applicable",
              "Communication providers",
              "Hosting and infrastructure providers",
              "Professional advisers where necessary",
              "Government authorities or law-enforcement agencies where required by applicable law",
              "Other parties where necessary to protect our legal rights, users, Services, or security",
            ]}
          />
          <Typography paragraph>
            We may also disclose information when required to comply with a
            legal obligation or valid legal process.
          </Typography>
        </>
      ),
    },
    {
      title: "11. Data Security",
      content: (
        <>
          <Typography paragraph>
            We take reasonable technical and organizational measures designed
            to protect personal information from unauthorized access,
            alteration, disclosure, loss, misuse, or destruction.
          </Typography>
          <Typography paragraph>Security measures may include:</Typography>
          <BulletList
            items={[
              "Access controls",
              "Authentication mechanisms",
              "Role-based permissions",
              "Secure server infrastructure",
              "Encryption or secure transmission where appropriate",
              "Monitoring and logging",
              "Regular technical maintenance",
            ]}
          />
          <Typography paragraph>
            However, no internet-based system can be guaranteed to be completely
            secure.
          </Typography>
          <Typography paragraph>
            Users should also maintain the confidentiality of their account
            credentials and immediately notify us if they suspect unauthorized
            access.
          </Typography>
        </>
      ),
    },
    {
      title: "12. Data Retention",
      content: (
        <>
          <Typography paragraph>
            We retain personal information only for as long as reasonably
            necessary for the purposes described in this Privacy Policy,
            including:
          </Typography>
          <BulletList
            items={[
              "Providing the Services",
              "Maintaining business and transaction records",
              "Resolving disputes",
              "Preventing fraud and misuse",
              "Maintaining security records",
              "Complying with legal, tax, accounting, or regulatory requirements",
            ]}
          />
          <Typography paragraph>
            When information is no longer required, we may delete, anonymize, or
            securely dispose of it, subject to applicable legal requirements.
          </Typography>
        </>
      ),
    },
    {
      title: "13. Your Privacy Rights",
      content: (
        <>
          <Typography paragraph>
            Subject to applicable law, individuals may have rights relating to
            their personal information, including rights to:
          </Typography>
          <BulletList
            items={[
              "Request information about personal data processed by us",
              "Request correction of inaccurate information",
              "Request deletion of personal information where legally applicable",
              "Withdraw consent where processing is based on consent",
              "Request information regarding how personal data is processed",
              "Raise a privacy-related grievance",
              "Exercise other rights available under applicable data protection laws",
            ]}
          />
          <Typography paragraph>
            Where consent is the basis for processing, withdrawal of consent may
            affect our ability to provide certain Services.
          </Typography>
          <Typography paragraph>
            Requests may be submitted using the contact information provided in
            this Privacy Policy.
          </Typography>
        </>
      ),
    },
    {
      title: "14. Children's Privacy",
      content: (
        <>
          <Typography paragraph>
            RestaurantWala is intended for businesses, restaurant operators,
            staff, and customers using restaurant ordering services.
          </Typography>
          <Typography paragraph>
            We do not knowingly request or intentionally collect personal
            information from children for independent account creation or
            unrelated purposes.
          </Typography>
          <Typography paragraph>
            If you believe that a child has provided personal information to us
            without appropriate authorization, please contact us so that we can
            review and take appropriate action.
          </Typography>
        </>
      ),
    },
    {
      title: "15. Data Breach and Security Incidents",
      content: (
        <>
          <Typography paragraph>
            If we become aware of a security incident involving personal
            information, we will assess the incident and take reasonable steps
            as required by applicable law, including implementing measures to
            contain, investigate, and mitigate the incident.
          </Typography>
          <Typography paragraph>
            Where legally required, we may notify affected individuals,
            relevant authorities, or other parties.
          </Typography>
        </>
      ),
    },
    {
      title: "16. Account Termination",
      content: (
        <>
          <Typography paragraph>
            If a restaurant account is closed or terminated, certain information
            may continue to be retained where necessary for:
          </Typography>
          <BulletList
            items={[
              "Legal compliance",
              "Accounting and tax requirements",
              "Fraud prevention",
              "Dispute resolution",
              "Security purposes",
              "Enforcement of agreements",
              "Legitimate business records",
            ]}
          />
          <Typography paragraph>
            Other information may be deleted or anonymized in accordance with
            our retention practices.
          </Typography>
        </>
      ),
    },
    {
      title: "17. Changes to This Privacy Policy",
      content: (
        <>
          <Typography paragraph>
            We may update this Privacy Policy from time to time to reflect
            changes in:
          </Typography>
          <BulletList
            items={[
              "Our Services",
              "Technology",
              "Business practices",
              "Legal or regulatory requirements",
              "Privacy and security practices",
            ]}
          />
          <Typography paragraph>
            When we make material changes, we may update the "Last Updated" date
            and, where appropriate, provide additional notice.
          </Typography>
          <Typography paragraph>
            We encourage users to review this Privacy Policy periodically.
          </Typography>
        </>
      ),
    },
    {
      title: "18. Governing Law",
      content: (
        <>
          <Typography paragraph>
            This Privacy Policy shall be governed by and interpreted in
            accordance with the applicable laws of India.
          </Typography>
          <Typography paragraph>
            Any disputes relating to this Privacy Policy or the Services shall
            be subject to the applicable jurisdiction of the courts having
            authority under Indian law.
          </Typography>
        </>
      ),
    },
  ];

  return (
    <>
    <PageTitle
        title="Privacy Policy – RestaurantWala"
        description="Read RestaurantWala's Privacy Policy to understand how we collect, use, protect and manage your information when you use our services."
        keywords="RestaurantWala privacy policy, privacy policy, data privacy, restaurant software privacy"
      />
      <Navbar />

      <PageBanner
        title="Privacy Policy"
        subtitle="Your privacy matters to us. Learn how RestaurantWala collects, uses, and protects your information."
        chipLabel="PRIVACY"
        bgImage={privacyBanner}
      />

      <Box sx={{ bgcolor: "#ffffff", py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              borderRadius: "24px",
              border: "1px solid var(--border)",
              bgcolor: "#fff",
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.6rem", md: "2rem" },
                fontWeight: 800,
                color: "var(--text)",
                mb: 1,
              }}
            >
              Privacy Policy
            </Typography>

            {/* <Typography
              sx={{
                color: "var(--text-light)",
                fontSize: "14px",
                mb: 4,
              }}
            >
              Last Updated: September 3, 2026
            </Typography> */}

            <Typography paragraph>
              RestaurantWala ("RestaurantWala", "we", "us", or "our") respects
              your privacy and is committed to protecting the personal
              information you provide while using our website, platform,
              applications, and restaurant management services (collectively,
              the "Services").
            </Typography>

            <Typography paragraph>
              This Privacy Policy explains what information we collect, how we
              use it, how we protect it, and the choices available to you.
            </Typography>

            <Typography paragraph>
              By accessing or using RestaurantWala, you acknowledge that you
              have read and understood this Privacy Policy.
            </Typography>

            <Divider sx={{ my: 4 }} />

            {sections.map((section) => (
              <Box key={section.title} sx={{ mb: 4 }}>
                <Typography
                //   variant="h5"
                //   sx={{
                //     fontWeight: 800,
                //     color: "var(--text)",
                //     fontSize: { xs: "1.15rem", md: "1.35rem" },
                //     mb: 2,
                //   }}
                 variant="h5"
                  sx={{
                    fontWeight: 750,
                    mb: 1.5,
                    fontSize: { xs: "1.25rem", md: "1.5rem" }
                  }}
                >
                  {section.title}
                </Typography>
                <Box
                  sx={{
                    color: "var(--text-light)",
                    fontSize: "14px",
                    lineHeight: 1.8,
                    "& p": { lineHeight: 1.8 },
                  }}
                >
                  {section.content}
                </Box>
              </Box>
            ))}

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "var(--text)",
                  fontSize: { xs: "1.15rem", md: "1.35rem" },
                  mb: 2,
                }}
              >
                19. Contact Us
              </Typography>

              <Typography paragraph>
                If you have questions, concerns, requests, or complaints
                regarding this Privacy Policy or the handling of personal
                information, you can contact RestaurantWala.
              </Typography>

              <Office
                title="Head Office"
                address={[
                  "Shiv Manglam 6, AB 23, Kalwar Rd,",
                  "Govindpura, Jhotwara, Hathod,",
                  "Jaipur, Rajasthan - 302013",
                ]}
              />

              <Office
                title="Branch Office"
                address={[
                  "Tharhounda Showroom, Gawdi Mod,",
                  "Neem Ka Thana, Rajasthan - 332713",
                ]}
              />

              <Box sx={{ mt: 3 }}>
                <ContactRow
                  icon={<Email />}
                  label="Email"
                  value="restaurantwala24@gmail.com"
                />
                <ContactRow
                  icon={<Phone />}
                  label="Phone"
                  value="+91 6378788756"
                />
              </Box>
            </Box>

            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: "var(--text)",
                  fontSize: { xs: "1.15rem", md: "1.35rem" },
                  mb: 2,
                }}
              >
                20. Privacy Grievances
              </Typography>

              <Typography paragraph>
                For privacy-related questions or requests, please contact us
                using the email address above.
              </Typography>

              <Typography paragraph>
                Please include sufficient information in your request to help us
                identify the relevant account, order, or communication, while
                avoiding sending unnecessary sensitive information.
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Footer />
    </>
  );
};

const BulletList = ({ items }) => (
  <Box
    component="ul"
    variant="body1"
    sx={{
      pl: 3,
      mt: 1,
      mb: 2,
      "& li": {
        mb: 0.7,
        color: "var(--text-light)",
      },
    }}
  >
    {items.map((item) => (
      <li key={item}>{item}</li>
    ))}
  </Box>
);

const Office = ({ title, address }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
    <LocationOn
      sx={{
        color: "var(--primary)",
        fontSize: 28,
        mt: 0.2,
        flexShrink: 0,
      }}
    />
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: "var(--text)",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {address.map((line) => (
        <Typography
          key={line}
          sx={{
            color: "var(--text-light)",
            fontSize: "14px",
            lineHeight: 1.7,
          }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  </Box>
);

const ContactRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
    <Box sx={{ color: "var(--primary)", display: "flex" }}>{icon}</Box>
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: "var(--text)",
          fontSize: "14px",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ color: "var(--text-light)", fontSize: "14px" }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

export default PrivacyPolicy;
